import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import get from 'lodash.get';

// Components
import Header from '../Headers/BasicHeader';
import FollowingRefetchWrapper from './FollowingRefetchWrapper';
import Modals from '../Modals/Modals';
import Recommendations from '../Recommendations/Recommendations';

// Helpers
import actions from '../../actions/FavoritesActions';
import trackPageView from '../../helpers/trackPageView';

// Mutations
import ModifyFollowingItems from '../../mutations/ModifyFollowingItems';

class FollowingContainer extends React.Component {
    constructor() {
        super();

        this.followingMutations = this.followingMutations.bind(this);

        this.state = {
            favoritesItems: {},
        };

        this.edges = null;
    }

    componentWillMount() {
        if (this.props.tabChanged) {
            this.props.resetTotalResults();
            this.props.setMutationResponse('');
        }
    }

    componentDidMount() {
        trackPageView();
    }

    componentDidUpdate(prevProps) {
        const { routerNavigation, isLoadingFullPage } = this.props;
        // hide page overlay loader when navigating through router
        if (routerNavigation && !prevProps.routerNavigation && isLoadingFullPage) {
            this.props.setFullPageLoading(false);
        }
    }

    followingMutations({ action }) {
        const { relay, userId, selectedItems } = this.props;
        const selectedItem = get(selectedItems, '[0]') || {};

        this.props.setFullPageLoading(true);
        this.props.setModalOpen(false);
        this.props.setShowErrorSuccessModal(false);

        ModifyFollowingItems.commit(relay.environment, {
            type: selectedItem.type,
            query: selectedItem.query,
            page: selectedItem.page,
            action,
            favoriteId: selectedItem.itemId,
            userId,
        })
            .then(() => {
                this.props.relay.refetch(
                    fragmentVariables => fragmentVariables,
                    null,
                    () => {
                        this.props.setFullPageLoading(false);
                        this.props.selectItems([]);
                    },
                    { force: true }
                );
            })
            .catch(() => {
                this.props.setFullPageLoading(false);
                this.props.selectItems([]);
            });
    }

    render() {
        const { viewer, hasUserId, launchModal, totalResults, userId } = this.props;

        if (totalResults === 0 || !viewer.user) {
            return (
                <Recommendations
                    hasUserId={hasUserId}
                    trending={viewer}
                    userId={userId}
                    viewer={viewer}
                />
            );
        } else {
            return (
                <Fragment>
                    <Header title="Following" totalItems={totalResults} />
                    <FollowingRefetchWrapper
                        launchModal={launchModal}
                        user={viewer.user || null}
                        userId={userId}
                    />
                    <Modals
                        followingMutations={this.followingMutations}
                        modalOpen={this.props.modalOpen}
                        mutationResponse={this.props.mutationResponse}
                        openModalType={this.props.openModalType}
                        showErrorSuccessModal={this.props.showErrorSuccessModal}
                        selectedItems={this.props.selectedItems}
                    />
                </Fragment>
            );
        }
    }
}

FollowingContainer.propTypes = {
    isLoadingFullPage: PropTypes.bool,
    hasUserId: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    modalOpen: PropTypes.bool,
    mutationResponse: PropTypes.string,
    openModalType: PropTypes.string,
    launchModal: PropTypes.func,
    resetUiState: PropTypes.func,
    resetTotalResults: PropTypes.func,
    routerNavigation: PropTypes.bool,
    selectItems: PropTypes.func,
    selectedItems: PropTypes.array,
    setFullPageLoading: PropTypes.func,
    setModalOpen: PropTypes.func,
    setMutationResponse: PropTypes.func,
    setNumItems: PropTypes.func,
    setOpenModalType: PropTypes.func,
    setShowErrorSuccessModal: PropTypes.func,
    showErrorSuccessModal: PropTypes.bool,
    tabChanged: PropTypes.bool,
    totalResults: PropTypes.number,
    userId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
    return {
        modalOpen: state.favorites.modalState.modalOpen,
        mutationResponse: state.favorites.uiState.mutationResponse,
        openModalType: state.favorites.modalState.openModalType,
        tabChanged: state.favorites.uiState.tabChanged,
        showErrorSuccessModal: state.favorites.modalState.showErrorSuccessModal,
        totalResults: state.favorites.uiState.totalResults,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        resetUiState: () => {
            dispatch(actions.resetUiState());
        },
        resetTotalResults: () => {
            dispatch(actions.setTotalResults({ totalResults: -1 }));
        },
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setFullPageLoading: isLoadingFullPage => {
            dispatch(actions.setFullPageLoading({ isLoadingFullPage }));
        },
        setModalOpen: modalOpen => {
            dispatch(actions.setModalOpen({ modalOpen }));
        },
        setMutationResponse: mutationResponse => {
            dispatch(actions.setMutationResponse({ mutationResponse }));
        },
        setNumItems: first => {
            dispatch(actions.setNumItems({ first }));
        },
        setOpenModalType: openModalType => {
            dispatch(actions.setOpenModalType({ openModalType }));
        },
        setShowErrorSuccessModal: showErrorSuccessModal => {
            dispatch(actions.setShowErrorSuccessModal({ showErrorSuccessModal }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(FollowingContainer);
FollowingContainer = createRefetchContainer(
    ReduxWrapped,
    {
        viewer: graphql`
            fragment FollowingContainer_viewer on Viewer {
                ...Recommendations_trending
                user(userId: $userId) @include(if: $hasUserId) {
                    ...FollowingRefetchWrapper_user
                }
            }
        `,
    },
    graphql`
        query FollowingContainerRefetchQuery(
            $after: String
            $loadPortfolioData: Boolean!
            $selectedItemIds: [String]!
            $hasUserId: Boolean!
            $userId: String!
            $userIds: [String]!
        ) {
            viewer {
                ...FollowingContainer_viewer
            }
        }
    `
);

export default FollowingContainer;
